import { authRequest } from '@/lib/deep-mining/request';

type IMemoryItem = MISC.MemoryItem;
type IMemoryAgentItem = MISC.MemoryAgentItem;

// 1.1 记忆空间初始化
export const initMemorySpace = async (): Promise<{
  default_enabled: boolean;
}> => {
  return authRequest('/business/memories/init');
};

// 1.1.1 记忆空间心跳（轮询）
export const memorySpaceHeartbeat = async (): Promise<void> => {
  return authRequest('/business/memories/heartbeat', {
    method: 'post',
  });
};

// 1.2 获取记忆列表
export const getMemoryList = async (params?: {
  page?: number;
  page_size?: number;
  status?: MISC.MemoryStatus;
  agent_key?: string;
  search?: string;
}): Promise<{
  items: IMemoryItem[];
  total: number;
}> => {
  const { page = 1, page_size = 10, status, agent_key, search } = params || {};
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(page_size),
    ...(status ? { status } : {}),
    ...(agent_key ? { agent_key } : {}),
    ...(search ? { search } : {}),
  });
  return authRequest(`/business/memories?${query}`);
};

// 1.3 获取记忆详情
export const getMemoryDetail = async (
  memoryId: string,
): Promise<IMemoryItem> => {
  return authRequest(`/business/memories/${memoryId}`);
};

// 1.4 创建记忆（手动）
export const createMemory = async (memory: {
  title: string;
  content: string;
  memory_type?: MISC.MemoryExperienceType;
  agent_key?: string;
}): Promise<IMemoryItem> => {
  return authRequest('/business/memories', {
    method: 'post',
    body: JSON.stringify(memory),
  });
};

// 1.5 设置默认启用状态（写入模式切换）
export const setDefaultEnabledSetting = async (
  enabled: boolean,
): Promise<void> => {
  return authRequest('/business/memories/default-enabled-setting', {
    method: 'post',
    body: JSON.stringify({ default_enabled: enabled }),
  });
};

// 1.5.1 获取用户 Agent 列表
export const getMemoryAgents = async (): Promise<IMemoryAgentItem[]> => {
  return authRequest('/business/memories/agents');
};

// 1.6 编辑记忆
export const updateMemory = async (
  memoryId: string,
  memory: {
    title?: string;
    content?: string;
    memory_type?: MISC.MemoryExperienceType;
    agent_key?: string;
  },
): Promise<IMemoryItem> => {
  return authRequest(`/business/memories`, {
    method: 'put',
    body: JSON.stringify({ memory_id: memoryId, ...memory }),
  });
};

// 1.7 删除记忆
export const deleteMemory = async (memoryId: string): Promise<void> => {
  return authRequest(`/business/memories/${memoryId}`, {
    method: 'delete',
  });
};

// 1.8 启用/禁用记忆
export const toggleMemory = async (
  memoryId: string,
  status: 'active' | 'disabled',
): Promise<void> => {
  return authRequest(`/business/memories/${memoryId}/toggle`, {
    method: 'post',
    body: JSON.stringify({ status }),
    throwError: true,
  });
};

// 1.9 标记记忆为已读
export const markMemoryAsRead = async (memoryId: string): Promise<void> => {
  return authRequest(`/business/memories/${memoryId}/read`, {
    method: 'post',
  });
};

// 对话记忆快照（点击「已获取 X 段记忆」时展示历史快照）
export interface MemorySnapshot {
  id: string;
  memory_id: string;
  title: string;
  content: string;
  memory_type: string;
  agent_id: string;
  agent_name: string;
  memory_updated_at: string;
  created_at: string;
  status?: 'active' | 'disabled' | 'deleted';
}
// 获取快照记忆列表
export const getConversationMemories = async (
  snapshotIds: string[],
): Promise<MemorySnapshot[]> => {
  return authRequest(`/business/memories/snapshots`, {
    method: 'post',
    body: JSON.stringify({ snapshot_ids: snapshotIds }),
  });
};
export const getMemoryTypeOptions = async (): Promise<
  Array<{ value: string; label: string }>
> => {
  return authRequest('/business/memories/type-options');
};

// 获取当前账号记忆空间的未读红点状态
export const getMemoryUnread = async (): Promise<{ unread: boolean }> => {
  return authRequest('/business/memories/unread');
};
